import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { billingService } from '../services/billingService'
import type { BillableItem, BillingAccount, BillingPayment, BillingStatement } from '../types/billing'
import './Billing.css'

export function Billing() {
  const queryClient = useQueryClient()
  const [patientIdInput, setPatientIdInput] = useState('')
  const [activePatientId, setActivePatientId] = useState('')
  const [selectedAccountId, setSelectedAccountId] = useState('')

  const [itemDescription, setItemDescription] = useState('')
  const [itemAmount, setItemAmount] = useState('')
  const [itemConsultationId, setItemConsultationId] = useState('')
  const [itemProvider, setItemProvider] = useState<'manual' | 'stripe' | 'square'>('manual')

  const [paymentAmount, setPaymentAmount] = useState('')
  const [paymentProvider, setPaymentProvider] = useState<'manual' | 'stripe' | 'square'>('manual')

  const [statementDate, setStatementDate] = useState('')
  const [statementPeriodStart, setStatementPeriodStart] = useState('')
  const [statementPeriodEnd, setStatementPeriodEnd] = useState('')
  const [statementTotal, setStatementTotal] = useState('')

  const formatMoney = (value: number, currency = 'EUR') => {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value)
  }

  const { data: accounts = [] } = useQuery<BillingAccount[]>({
    queryKey: ['billing-accounts', activePatientId],
    queryFn: () => billingService.getAccounts({ patientId: activePatientId }),
    enabled: !!activePatientId,
  })

  const primaryAccount = useMemo(() => accounts[0], [accounts])

  useEffect(() => {
    if (!selectedAccountId && primaryAccount?.id) {
      setSelectedAccountId(primaryAccount.id)
    }
  }, [primaryAccount, selectedAccountId])

  const { data: items = [] } = useQuery<BillableItem[]>({
    queryKey: ['billing-items', selectedAccountId || activePatientId],
    queryFn: () => billingService.getItems({
      accountId: selectedAccountId || undefined,
      patientId: selectedAccountId ? undefined : activePatientId || undefined,
    }),
    enabled: !!(selectedAccountId || activePatientId),
  })

  const { data: payments = [] } = useQuery<BillingPayment[]>({
    queryKey: ['billing-payments', selectedAccountId || activePatientId],
    queryFn: () => billingService.getPayments({
      accountId: selectedAccountId || undefined,
      patientId: selectedAccountId ? undefined : activePatientId || undefined,
    }),
    enabled: !!(selectedAccountId || activePatientId),
  })

  const { data: statements = [] } = useQuery<BillingStatement[]>({
    queryKey: ['billing-statements', selectedAccountId || activePatientId],
    queryFn: () => billingService.getStatements({
      accountId: selectedAccountId || undefined,
      patientId: selectedAccountId ? undefined : activePatientId || undefined,
    }),
    enabled: !!(selectedAccountId || activePatientId),
  })

  const createAccountMutation = useMutation({
    mutationFn: () => billingService.createAccount({ patientId: activePatientId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['billing-accounts', activePatientId] })
    },
  })

  const createItemMutation = useMutation({
    mutationFn: () => billingService.createItem({
      patientId: activePatientId,
      accountId: selectedAccountId || undefined,
      consultationId: itemConsultationId || undefined,
      description: itemDescription,
      amount: Number(itemAmount),
      provider: itemProvider,
    }),
    onSuccess: () => {
      setItemDescription('')
      setItemAmount('')
      setItemConsultationId('')
      queryClient.invalidateQueries({ queryKey: ['billing-items', selectedAccountId || activePatientId] })
    },
  })

  const createPaymentMutation = useMutation({
    mutationFn: () => billingService.createPayment({
      patientId: selectedAccountId ? undefined : activePatientId,
      accountId: selectedAccountId || undefined,
      amount: Number(paymentAmount),
      provider: paymentProvider,
    }),
    onSuccess: () => {
      setPaymentAmount('')
      queryClient.invalidateQueries({ queryKey: ['billing-payments', selectedAccountId || activePatientId] })
    },
  })

  const createStatementMutation = useMutation({
    mutationFn: () => billingService.createStatement({
      patientId: selectedAccountId ? undefined : activePatientId,
      accountId: selectedAccountId || undefined,
      statementDate,
      periodStart: statementPeriodStart || undefined,
      periodEnd: statementPeriodEnd || undefined,
      totalAmount: statementTotal ? Number(statementTotal) : undefined,
    }),
    onSuccess: () => {
      setStatementDate('')
      setStatementPeriodStart('')
      setStatementPeriodEnd('')
      setStatementTotal('')
      queryClient.invalidateQueries({ queryKey: ['billing-statements', selectedAccountId || activePatientId] })
    },
  })

  const handlePatientSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setActivePatientId(patientIdInput.trim())
    setSelectedAccountId('')
  }

  return (
    <div className="billing-page">
      <header className="billing-header">
        <div>
          <h1>Billing</h1>
          <p>Manage patient accounts, billable items, payments, and statements.</p>
        </div>
      </header>

      <section className="billing-grid">
        <div className="billing-card">
          <div className="billing-card-header">
            <h2>Patient Account</h2>
            <span className="billing-card-subtitle">Load a patient to view account data.</span>
          </div>
          <form className="billing-form" onSubmit={handlePatientSearch}>
            <label>
              <span>Patient ID</span>
              <input
                type="text"
                value={patientIdInput}
                onChange={(event) => setPatientIdInput(event.target.value)}
                placeholder="Enter patient ID"
              />
            </label>
            <button className="btn-primary" type="submit">Load Account</button>
          </form>
          {activePatientId && (
            <div className="billing-account-card">
              <div className="billing-account-row">
                <span>Patient</span>
                <strong>{activePatientId}</strong>
              </div>
              {primaryAccount ? (
                <>
                  <div className="billing-account-row">
                    <span>Account</span>
                    <strong>{primaryAccount.id}</strong>
                  </div>
                  <div className="billing-account-row">
                    <span>Status</span>
                    <strong className={`billing-status billing-status-${primaryAccount.status}`}>
                      {primaryAccount.status}
                    </strong>
                  </div>
                  <div className="billing-account-row billing-balance">
                    <span>Balance</span>
                    <strong>{formatMoney(primaryAccount.balance, primaryAccount.currency)}</strong>
                  </div>
                </>
              ) : (
                <div className="billing-empty">
                  <p>No billing account found.</p>
                  <button
                    className="btn-secondary"
                    type="button"
                    onClick={() => createAccountMutation.mutate()}
                    disabled={createAccountMutation.isPending}
                  >
                    {createAccountMutation.isPending ? 'Creating...' : 'Create Account'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="billing-card">
          <div className="billing-card-header">
            <h2>Create Billable Item</h2>
            <span className="billing-card-subtitle">Add fees or services to the account.</span>
          </div>
          <form
            className="billing-form billing-form-grid"
            onSubmit={(event) => {
              event.preventDefault()
              if (!activePatientId || !itemDescription || !itemAmount) return
              createItemMutation.mutate()
            }}
          >
            <label>
              <span>Description</span>
              <input value={itemDescription} onChange={(event) => setItemDescription(event.target.value)} />
            </label>
            <label>
              <span>Amount</span>
              <input type="number" step="0.01" value={itemAmount} onChange={(event) => setItemAmount(event.target.value)} />
            </label>
            <label>
              <span>Consultation ID</span>
              <input value={itemConsultationId} onChange={(event) => setItemConsultationId(event.target.value)} placeholder="Optional" />
            </label>
            <label>
              <span>Provider</span>
              <select value={itemProvider} onChange={(event) => setItemProvider(event.target.value as 'manual' | 'stripe' | 'square')}>
                <option value="manual">Manual</option>
                <option value="stripe">Stripe</option>
                <option value="square">Square</option>
              </select>
            </label>
            <button className="btn-primary" type="submit" disabled={createItemMutation.isPending}>
              {createItemMutation.isPending ? 'Adding...' : 'Add Item'}
            </button>
          </form>
        </div>
      </section>

      <section className="billing-grid">
        <div className="billing-card">
          <div className="billing-card-header">
            <h2>Record Payment</h2>
            <span className="billing-card-subtitle">Apply patient payments to the account.</span>
          </div>
          <form
            className="billing-form billing-form-grid"
            onSubmit={(event) => {
              event.preventDefault()
              if (!paymentAmount || !activePatientId) return
              createPaymentMutation.mutate()
            }}
          >
            <label>
              <span>Amount</span>
              <input type="number" step="0.01" value={paymentAmount} onChange={(event) => setPaymentAmount(event.target.value)} />
            </label>
            <label>
              <span>Provider</span>
              <select value={paymentProvider} onChange={(event) => setPaymentProvider(event.target.value as 'manual' | 'stripe' | 'square')}>
                <option value="manual">Manual</option>
                <option value="stripe">Stripe</option>
                <option value="square">Square</option>
              </select>
            </label>
            <button className="btn-primary" type="submit" disabled={createPaymentMutation.isPending}>
              {createPaymentMutation.isPending ? 'Recording...' : 'Record Payment'}
            </button>
          </form>
        </div>

        <div className="billing-card">
          <div className="billing-card-header">
            <h2>Generate Statement</h2>
            <span className="billing-card-subtitle">Issue a statement of account.</span>
          </div>
          <form
            className="billing-form billing-form-grid"
            onSubmit={(event) => {
              event.preventDefault()
              if (!statementDate || !activePatientId) return
              createStatementMutation.mutate()
            }}
          >
            <label>
              <span>Statement Date</span>
              <input type="date" value={statementDate} onChange={(event) => setStatementDate(event.target.value)} required />
            </label>
            <label>
              <span>Period Start</span>
              <input type="date" value={statementPeriodStart} onChange={(event) => setStatementPeriodStart(event.target.value)} />
            </label>
            <label>
              <span>Period End</span>
              <input type="date" value={statementPeriodEnd} onChange={(event) => setStatementPeriodEnd(event.target.value)} />
            </label>
            <label>
              <span>Total Amount</span>
              <input type="number" step="0.01" value={statementTotal} onChange={(event) => setStatementTotal(event.target.value)} />
            </label>
            <button className="btn-primary" type="submit" disabled={createStatementMutation.isPending}>
              {createStatementMutation.isPending ? 'Generating...' : 'Generate Statement'}
            </button>
          </form>
        </div>
      </section>

      <section className="billing-card">
        <div className="billing-card-header">
          <h2>Account Activity</h2>
          <span className="billing-card-subtitle">Recent items, payments, and statements.</span>
        </div>
        <div className="billing-activity">
          <div className="billing-table">
            <div className="billing-table-header">
              <h3>Billable Items</h3>
              <span className="billing-count">{items.length}</span>
            </div>
            {items.length === 0 ? (
              <p className="billing-muted">No items found.</p>
            ) : (
              <div className="billing-table-body">
                {items.map((item) => (
                  <div className="billing-row" key={item.id}>
                    <div>
                      <div className="billing-row-title">{item.description}</div>
                      <div className="billing-row-meta">{item.consultationId ? `Consultation ${item.consultationId}` : 'General'}</div>
                    </div>
                    <div className="billing-row-amount">{formatMoney(item.amount, item.currency)}</div>
                    <div className={`billing-chip billing-chip-${item.status}`}>{item.status}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="billing-table">
            <div className="billing-table-header">
              <h3>Payments</h3>
              <span className="billing-count">{payments.length}</span>
            </div>
            {payments.length === 0 ? (
              <p className="billing-muted">No payments recorded.</p>
            ) : (
              <div className="billing-table-body">
                {payments.map((payment) => (
                  <div className="billing-row" key={payment.id}>
                    <div>
                      <div className="billing-row-title">{payment.paymentDate.split('T')[0]}</div>
                      <div className="billing-row-meta">{payment.provider}</div>
                    </div>
                    <div className="billing-row-amount">{formatMoney(payment.amount, payment.currency)}</div>
                    <div className={`billing-chip billing-chip-${payment.status}`}>{payment.status}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="billing-table">
            <div className="billing-table-header">
              <h3>Statements</h3>
              <span className="billing-count">{statements.length}</span>
            </div>
            {statements.length === 0 ? (
              <p className="billing-muted">No statements generated.</p>
            ) : (
              <div className="billing-table-body">
                {statements.map((statement) => (
                  <div className="billing-row" key={statement.id}>
                    <div>
                      <div className="billing-row-title">{statement.statementDate}</div>
                      <div className="billing-row-meta">{statement.itemIds.length} item(s)</div>
                    </div>
                    <div className="billing-row-amount">{formatMoney(statement.totalAmount)}</div>
                    <div className={`billing-chip billing-chip-${statement.status}`}>{statement.status}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  )
}
